import {
  Action,
  ActionChangedDetails,
  BASE_UPDATED_AT_HEADER,
  Feedback,
  IDEMPOTENCY_KEY_HEADER,
  Plan,
  PlanRun,
  R1PrepChangedDetails,
  R1PrepResult,
} from '@cockpit/shared';
import { ApiError, request, uploadFile } from '../api/client';
import { executeBody, initialDrafts } from '../plans';
import {
  addedItemId,
  createdIdMap,
  fieldConflicts,
  nextRunnable,
  r1FieldConflicts,
  remapIds,
  type QueuedOp,
} from './ops';

/**
 * Lot AW : envoi des changements gardés hors connexion, dans l'ordre, au retour du réseau. Chaque requête porte la
 * clé d'idempotence du changement (un renvoi après une réponse perdue ne refait rien) et, pour une carte ou les
 * infos d'un R1, la version vue par la personne (409 si quelqu'un l'a changée entre-temps).
 */

/** Ce qu'un envoi réussi rapporte : identifiants provisoires remplacés, changement mis à jour (retour en cours). */
export interface SendOutcome {
  idMap?: Record<string, string>;
  /** Changement à garder (envoi en plusieurs temps : retour créé, photos pas toutes parties). */
  progress?: QueuedOp;
}

export type SendFn = (op: QueuedOp, save: (op: QueuedOp) => Promise<void>) => Promise<SendOutcome>;

function key(op: QueuedOp, suffix?: string): Record<string, string> {
  return { [IDEMPOTENCY_KEY_HEADER]: suffix ? `${op.id}:${suffix}` : op.id };
}

/** Envoie un changement (requêtes du lot AS, AO, AQ, avec les en-têtes du lot AW). */
export function createSender(blobs: { get(id: string): Promise<Blob | undefined> }): SendFn {
  return async (op, save) => {
    const enc = encodeURIComponent;
    switch (op.kind) {
      case 'action.create': {
        const created = await request('POST', '/api/actions', {
          body: op.body,
          schema: Action,
          headers: key(op),
        });
        return { idMap: createdIdMap(op, created) };
      }
      case 'action.update':
        await request('PATCH', `/api/actions/${enc(op.actionId)}`, {
          body: op.body,
          schema: Action,
          headers: { ...key(op), [BASE_UPDATED_AT_HEADER]: op.base.updatedAt ?? '' },
        });
        return {};
      case 'action.done': {
        // « Fait » sur une tâche manuelle : le plan `action.done` (une seule étape interne), sans écran.
        const plan = await request('POST', '/api/plans', {
          body: { kind: 'action.done', refs: { actionId: op.actionId } },
          schema: Plan,
          headers: key(op, 'plan'),
        });
        const drafts = initialDrafts(plan).map((d) => ({ ...d, checked: true }));
        const run = await request('POST', `/api/plans/${enc(plan.id)}/execute`, {
          body: executeBody(plan, drafts),
          schema: PlanRun,
          headers: key(op, 'exec'),
        });
        if (run.plan.status !== 'DONE') {
          throw new ApiError(422, 'PLAN_NOT_DONE', '« Fait » n’a pas pu être enregistré.');
        }
        return {};
      }
      case 'checklist.add': {
        const action = await request('POST', `/api/actions/${enc(op.actionId)}/checklist`, {
          body: { label: op.label },
          schema: Action,
          headers: key(op),
        });
        const real = addedItemId(op, action);
        return real ? { idMap: { [op.itemId]: real } } : {};
      }
      case 'checklist.update':
        await request('PATCH', `/api/actions/${enc(op.actionId)}/checklist/${enc(op.itemId)}`, {
          body: op.body,
          schema: Action,
          headers: key(op),
        });
        return {};
      case 'checklist.delete':
        await request('DELETE', `/api/actions/${enc(op.actionId)}/checklist/${enc(op.itemId)}`, {
          schema: Action,
          headers: key(op),
        });
        return {};
      case 'r1.prep':
        await request('PUT', `/api/r1/${enc(op.r1Key)}/prep`, {
          body: op.input,
          schema: R1PrepResult,
          headers: { ...key(op), [BASE_UPDATED_AT_HEADER]: op.base.filledAt ?? '' },
        });
        return {};
      case 'feedback.create': {
        let current = op;
        if (!current.feedbackId) {
          const created = await request('POST', '/api/feedback', {
            body: current.body,
            schema: Feedback,
            headers: key(op),
          });
          current = { ...current, feedbackId: created.id };
          await save(current);
        }
        for (let i = current.uploaded; i < current.blobIds.length; i += 1) {
          const blob = await blobs.get(current.blobIds[i] as string);
          if (blob) {
            await uploadFile(
              `/api/feedback/${enc(current.feedbackId as string)}/attachments`,
              blob,
              Feedback,
              undefined,
              key(op, `att:${i}`),
            );
          }
          current = { ...current, uploaded: i + 1 };
          await save(current);
        }
        return { progress: current };
      }
      default:
        return {};
    }
  };
}

export interface QueueIO {
  list(): QueuedOp[];
  save(op: QueuedOp): Promise<void>;
  remove(op: QueuedOp): Promise<void>;
}

export interface ReplayReport {
  sent: number;
  conflicts: number;
  failed: number;
  /** Arrêt faute de réseau (ou session expirée) : la suite partira plus tard. */
  stopped: 'offline' | 'unauthenticated' | 'busy' | null;
}

const MAX_REBASE = 3;

/**
 * Envoie les changements un par un, dans l'ordre. Un changement en conflit (409 avec la version actuelle) est
 * comparé champ par champ : sans champ en commun, il repart aussitôt sur la nouvelle version ; sinon il attend un
 * choix (« Garder ta version » / « Garder la sienne »). Les autres refus le marquent « non envoyé » (« Réessayer » /
 * « Abandonner »). Un changement bloqué retient les suivants de la même carte, pas ceux des autres.
 */
export async function replayQueue(io: QueueIO, send: SendFn): Promise<ReplayReport> {
  const report: ReplayReport = { sent: 0, conflicts: 0, failed: 0, stopped: null };
  const rebases = new Map<string, number>();
  for (;;) {
    const op = nextRunnable(io.list());
    if (!op) break;
    try {
      const outcome = await send(op, io.save);
      await io.remove(outcome.progress ?? op);
      report.sent += 1;
      if (outcome.idMap && Object.keys(outcome.idMap).length > 0) {
        const before = io.list();
        const after = remapIds(before, outcome.idMap);
        for (const [index, next] of after.entries()) if (next !== before[index]) await io.save(next);
      }
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      if (err.status === 0) {
        report.stopped = 'offline';
        break;
      }
      if (err.status === 401) {
        report.stopped = 'unauthenticated';
        break;
      }
      if (err.status === 409 && err.code === 'IDEMPOTENCY_IN_PROGRESS') {
        report.stopped = 'busy';
        break;
      }
      // « Fait » sur une carte déjà fermée entre-temps : le but est atteint.
      if (op.kind === 'action.done' && err.status === 409 && err.code === 'ACTION_CLOSED') {
        await io.remove(op);
        report.sent += 1;
        continue;
      }
      // Étape déjà supprimée (par quelqu'un d'autre, ou réponse perdue) : le but est atteint.
      if (op.kind === 'checklist.delete' && err.status === 404 && err.code === 'CHECKLIST_ITEM_NOT_FOUND') {
        await io.remove(op);
        report.sent += 1;
        continue;
      }
      if (err.status === 409 && op.kind === 'action.update' && err.code === 'ACTION_CHANGED') {
        const details = ActionChangedDetails.safeParse(err.details);
        if (details.success) {
          const fields = fieldConflicts(op, details.data.current);
          const tries = rebases.get(op.id) ?? 0;
          if (fields.length === 0 && tries < MAX_REBASE) {
            rebases.set(op.id, tries + 1);
            await io.save({ ...op, base: { ...op.base, updatedAt: details.data.current.updatedAt } });
            continue;
          }
          await io.save({
            ...op,
            state: 'conflict',
            conflict: { current: details.data.current, changedBy: details.data.changedBy, fields },
          });
          report.conflicts += 1;
          continue;
        }
      }
      if (err.status === 409 && op.kind === 'r1.prep' && err.code === 'R1_PREP_CHANGED') {
        const details = R1PrepChangedDetails.safeParse(err.details);
        if (details.success) {
          const fields = r1FieldConflicts(op, details.data.current);
          const tries = rebases.get(op.id) ?? 0;
          if (fields.length === 0 && tries < MAX_REBASE) {
            rebases.set(op.id, tries + 1);
            await io.save({
              ...op,
              base: { ...op.base, filledAt: details.data.current.prep?.filledAt ?? null },
            });
            continue;
          }
          await io.save({
            ...op,
            state: 'conflict',
            conflict: { current: details.data.current, changedBy: details.data.changedBy, fields },
          });
          report.conflicts += 1;
          continue;
        }
      }
      const latest = io.list().find((o) => o.id === op.id) ?? op;
      await io.save({
        ...latest,
        state: 'failed',
        error: { status: err.status, code: err.code, message: err.message },
      });
      report.failed += 1;
    }
  }
  return report;
}
